//"use client"

import { useEffect, useRef, useState } from "react"
import { Helmet } from "react-helmet"
import { serviceSchemaJson } from "../utils/serviceSchema";
import { breadcrumbSchemaJson } from "../utils/breadcrumbSchema";
import "./RPAIPASolution.css"

function RPAIPASolution({ scrollToAppointment }) {
  const [isVisible, setIsVisible] = useState(false)
  const [counters, setCounters] = useState({ efficiency: 60, costReduction: 2, accuracy: 95 })
  const statsRef = useRef(null)

  // Intersection Observer for triggering animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          startCounterAnimation()
        }
      },
      { threshold: 0.3 },
    )

    if (statsRef.current) {
      observer.observe(statsRef.current)
    }

    return () => observer.disconnect()
  }, [])

  // Counter animation function
  const startCounterAnimation = () => {
    const targets = { efficiency: 60, costReduction: 2, accuracy: 95 }
    const duration = 2000 // 2 seconds
    const steps = 60
    const stepDuration = duration / steps
    let currentStep = 0

    const timer = setInterval(() => {
      currentStep++
      const progress = currentStep / steps
      const easeOutQuart = 1 - Math.pow(1 - progress, 4)

      setCounters({
        efficiency: Math.round(targets.efficiency * easeOutQuart),
        costReduction: Math.round(targets.costReduction * easeOutQuart),
        accuracy: Math.round(targets.accuracy * easeOutQuart),
      })

      if (currentStep >= steps) {
        clearInterval(timer)
        setCounters(targets)
      }
    }, stepDuration)
  }

  return (
    <div className="main_div_work_speaks">
      <Helmet>
<meta property="og:title" content="RPA & IPA Implementation Solutions | 360 SynergyTech" />
<meta property="og:url" content="https://360synergytech.com/rpa-ipa-implementation" />
<meta property="og:image" content="https://360synergytech.com/synergy-logo.png" />
<meta property="og:description" content="Automate business processes with 360 SynergyTech's RPA & IPA services. We deliver intelligent automation to boost efficiency and productivity." />

<meta name="twitter:card" content="summary_large_image" />
<meta name="twitter:title" content="RPA & IPA Implementation Solutions | 360 SynergyTech" />
<meta name="twitter:description" content="Automate business processes with 360 SynergyTech's RPA & IPA services. We deliver intelligent automation to boost efficiency and productivity." />
<meta name="twitter:image" content="https://360synergytech.com/synergy-logo.png" />

        <title>RPA & IPA Implementation Solutions | 360 SynergyTech</title>
        <meta name="description" content="Automate business processes with 360 SynergyTech's RPA & IPA services. We deliver intelligent automation to boost efficiency and productivity." />
        <link rel="canonical" href="https://360synergytech.com/rpa-ipa-implementation" />
        <script type="application/ld+json">{serviceSchemaJson({ name: "RPA & IPA Implementation", description: "Automate business processes with 360 SynergyTech's RPA & IPA services. We deliver intelligent automation to boost efficiency and productivity.", path: "/rpa-ipa-implementation" })}</script>
        <script type="application/ld+json">{breadcrumbSchemaJson([{ name: "Services", path: "/services" }, { name: "RPA & IPA Implementation", path: "/rpa-ipa-implementation" }])}</script>
      </Helmet>
      
      <div className="row m-0 p-0">
        <div className="col-lg-2 col-md-1 col-sm-12 col-12"></div>
        <div className="col-lg-8 col-md-10 col-sm-12 col-12">
          <div className="row m-0 p-0">
            <div className="col-lg-10 col-12">
              <h2 className="heading_work_speaks">RPA & IPA solutions for intelligent automation</h2>
              <p className="heading_work_speaks_para">
                At 360 SynergyTech, we provide cutting-edge RPA (Robotic Process Automation) and IPA (Intelligent Process Automation) solutions to streamline repetitive tasks and enhance operational efficiency. Our expertise in automation technologies ensures scalable, secure, and intelligent workflows tailored to your business needs. Whether you need process automation or AI-driven workflows, we deliver transformative solutions.
              </p>
            </div>
            <div className="col-lg-2 col-12">
              <div className="button-container">
                <button
                  className="btn_lets_talk"
                  onClick={scrollToAppointment}
                  aria-label="Schedule a consultation with our experts"
                >
                  Get Help
                </button>
              </div>
            </div>
          </div>

          <div style={{ marginTop: "100px" }}></div>

          <div ref={statsRef} className="stats_section">
            <p className="stats_title">Transforming businesses with intelligent automation solutions</p>
            <div className="stats_cards_container">
              <div className="stat_card stat_card_blue">
                <p className="stat_number">{`${counters.efficiency}%`}</p>
                <p className="stat_description">Increase in process efficiency</p>
              </div>
              <div className="stat_card stat_card_orange">
                <p className="stat_number">{`${counters.costReduction}X`}</p>
                <p className="stat_description">Reduction in operational costs</p>
              </div>
              <div className="stat_card stat_card_blue">
                <p className="stat_number">{`${counters.accuracy}%`}</p>
                <p className="stat_description">Automation accuracy rate</p>
              </div>
            </div>
          </div>

          <div style={{ marginTop: "80px" }}></div>

          {/* Content Cards Section */}
          <div className="content_cards_section">
            <div className="content_card">
              <div className="content_card_header">
                <h2 className="content_card_title">Robotic Process Automation (RPA)</h2>
              </div>
              <div className="content_card_body">
                <p className="content_card_text">
                  Automate repetitive manual tasks with RPA bots. We implement solutions for data entry, invoice processing, and other routine operations to save time and reduce errors.
                </p>
              </div>
            </div>

            <div className="content_card">
              <div className="content_card_header">
                <h2 className="content_card_title">Intelligent Process Automation (IPA)</h2>
              </div>
              <div className="content_card_body">
                <p className="content_card_text">
                  Combine RPA with AI for intelligent automation. Our IPA solutions include cognitive capabilities like natural language processing and decision-making for complex workflows.
                </p>
              </div>
            </div>

            <div className="content_card">
              <div className="content_card_header">
                <h2 className="content_card_title">Workflow Orchestration</h2>
              </div>
              <div className="content_card_body">
                <p className="content_card_text">
                  Optimize end-to-end business processes with workflow orchestration. We design automated workflows that integrate multiple systems and ensure smooth operations.
                </p>
              </div>
            </div>

            <div className="content_card">
              <div className="content_card_header">
                <h2 className="content_card_title">AI-Powered Decision Support</h2>
              </div>
              <div className="content_card_body">
                <p className="content_card_text">
                  Enhance decision-making with AI-powered insights within IPA. We develop tools that analyze data and provide recommendations to improve operational strategies.
                </p>
              </div>
            </div>

            <div className="content_card">
              <div className="content_card_header">
                <h2 className="content_card_title">Scalable Automation Frameworks</h2>
              </div>
              <div className="content_card_body">
                <p className="content_card_text">
                  Build scalable automation systems that grow with your business. Our frameworks support expansion and integration with emerging technologies for long-term success.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-2 col-md-1 col-sm-12 col-12"></div>
      </div>
    </div>
  )
}

export default RPAIPASolution
